/**
 * Optional closing invoice footer (billing contact, disclaimer, thank-you, etc.).
 * Stored in invoices.invoice_footer; org default in settings.invoices.default_invoice_footer.
 *
 * Customer-facing presentation has no forced section title — the freeform text
 * is the closing content. Renderers omit the section when empty.
 */

import { supabase } from "../supabase";
import { getEffectiveOrgSetting } from "./organizationSettings";
import { INVOICE_FOOTER_ADMIN_TITLE } from "./invoiceCommercialSections";
import { loadActorOrganizationSettings } from "./invoiceOrgSettings";

export function normalizeInvoiceFooter(value) {
  const text = String(value || "").trim();
  return text || "";
}

export function hasInvoiceFooter(value) {
  return Boolean(normalizeInvoiceFooter(value));
}

export function serializeInvoiceFooter(value) {
  const text = normalizeInvoiceFooter(value);
  return text || null;
}

export function getOrganizationInvoiceFooter(settings) {
  return normalizeInvoiceFooter(
    getEffectiveOrgSetting(settings, "invoices.default_invoice_footer", ""),
  );
}

export async function loadDefaultInvoiceFooter(actor, client = supabase) {
  try {
    const settings = await loadActorOrganizationSettings(actor, client);
    return getOrganizationInvoiceFooter(settings);
  } catch (err) {
    console.warn("[invoiceFooter] default fallback", err);
    return "";
  }
}

export function resolveInvoiceFooter({
  invoice = null,
  organizationInvoiceFooter = null,
} = {}) {
  const override = invoice?.invoice_footer_override === true;
  if (override) {
    return normalizeInvoiceFooter(invoice?.invoice_footer);
  }

  if (hasInvoiceFooter(organizationInvoiceFooter)) {
    return normalizeInvoiceFooter(organizationInvoiceFooter);
  }

  if (hasInvoiceFooter(invoice?.invoice_footer)) {
    return normalizeInvoiceFooter(invoice.invoice_footer);
  }

  return "";
}

/**
 * Presentation for Preview/PDF. No customer-facing title — text stands alone.
 * Admin UIs may pass { includeAdminTitle: true }.
 */
export function buildInvoiceFooterPresentation(raw, options = {}) {
  const text = normalizeInvoiceFooter(raw);
  if (!text) return null;

  const presentation = {
    text,
    blocks: [{ type: "text", text }],
  };

  if (options.includeAdminTitle) {
    presentation.title = options.title || INVOICE_FOOTER_ADMIN_TITLE;
  }

  return presentation;
}
